/**
 * Utility functions for shadcn/ui components
 */

import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, parse, isValid } from 'date-fns'

/**
 * Combines class names with Tailwind CSS conflict resolution
 * Uses clsx for conditional classes and tailwind-merge for deduplication
 * @param inputs - Class names to combine
 * @returns Merged class string
 * @example
 * cn("px-2 py-1", "px-4") // Returns "py-1 px-4"
 * cn("text-red-500", condition && "text-blue-500") // Conditionally applies classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format currency value with currency code
 * @param amount - Amount to format
 * @param currencyCode - Currency code (e.g., 'USD', 'INR')
 * @returns Formatted currency string
 * @example
 * formatCurrency(1000, 'INR') // Returns "₹1,000.00"
 * formatCurrency(50.5, 'USD') // Returns "$50.50"
 */
export function formatCurrency(amount: number, _currencyCode?: string): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch (error) {
    // Fallback if currency code is invalid
    return `₹${amount.toFixed(2)}`
  }
}

export function inclusiveToBase(amount: number, gstRate: number): number {
  return Math.round((amount / (1 + gstRate / 100)) * 100) / 100
}

export function baseToInclusive(base: number, gstRate: number): number {
  return Math.round((base * (1 + gstRate / 100)) * 100) / 100
}

/**
 * Converts a 'yyyy-MM-dd' string (the storage/API shape used across the app)
 * into a Date for the shared <DatePicker>, or undefined for an empty field.
 */
export function parseDateInput(value: string | null | undefined): Date | undefined {
  if (!value) return undefined
  const parsed = parse(value, 'yyyy-MM-dd', new Date())
  return isValid(parsed) ? parsed : undefined
}

/** Converts a <DatePicker> selection back to the 'yyyy-MM-dd' storage shape. */
export function formatDateInput(date: Date | undefined): string {
  return date && isValid(date) ? format(date, 'yyyy-MM-dd') : ''
}
